import React, { ReactNode } from "react"
import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache } from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import { createAuthLink } from "aws-appsync-auth-link"
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link"
import { merge } from "lodash"
import type { Common } from "lib/types"
import { useConfig } from "../../configuration/useConfig"
import { fetchAuthSession } from "aws-amplify/auth"
import { removeTypenameFromVariables } from "@apollo/client/link/remove-typename"
import { AwsRumClient } from "helpers/awsRumClient"

type Props = {
  children: ReactNode
}

const ApolloClientProvider = ({ children }: Props) => {
  const { awsRegion, graphqlApiUrl, rumAppMonitorId, rumIdentityPool } = useConfig().config
  const url: string = graphqlApiUrl
  const region: string = awsRegion
  const auth: Common.AuthOptions = {
    type: "OPENID_CONNECT",
    jwtToken: async () =>
      (await fetchAuthSession().then((res) =>
        res.tokens?.accessToken.toString()
      )) ?? ""
  }

  const parseCustomerAttribute = (value: string | object): object => {
    switch (typeof value) {
      case "string":
        return parseCustomerAttribute(JSON.parse(value))
      case "object":
        return value
      default:
        return {}
    }
  }

  const removeTypenameLink = removeTypenameFromVariables()

  const client = new ApolloClient({
    link: ApolloLink.from([
      removeTypenameLink,
      onError(({ graphQLErrors, networkError, operation }) => {
        const { skipRumLogging } = operation.getContext() || {};
        const rumClient = AwsRumClient(rumAppMonitorId, rumIdentityPool, awsRegion);

        if (graphQLErrors && !skipRumLogging) {
          graphQLErrors?.forEach((err) => {
            const operationName = operation?.operationName ?? "unknown"
            const route = window.location.pathname

            const enrichedMessage = [
              err.message,
              `Operation: ${operationName}`
            ].join(" | ")

            const error = new Error(enrichedMessage)
            error.name = "GraphQLError"
            error.stack = `Route: ${route} | Variables: ${JSON.stringify(operation.variables)}`
            rumClient.recordError(error)

            console.error(
              `[GraphQL error]: Message: ${err.message}, Location: ${JSON.stringify(
                err.locations
              )}, Path: ${err.path}`
            )
          })
        }

        if (networkError) {
          rumClient.recordError(networkError)
          console.log(`[Network error]: ${JSON.stringify(networkError)}`) // eslint-disable-line
        }
      }),
      createAuthLink({ url, region, auth }),
      createSubscriptionHandshakeLink({ url, region, auth })
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        ShippingFeeModificationUpdateData: {
          keyFields: false,
        },
        PaymentProvider: {
          keyFields: false,
        },
        Provider: {
          keyFields: false,
        },
        QueryProductManagement: {
          merge: true
        },
        QueryOrderManagement: {
          merge: true
        },
        OrderLineModificationUpdateData: {
          keyFields: false
        },
        OrderLineModificationUpdateDataPrice: {
          keyFields: false
        },
        OrderLineModificationCreateData: {
          keyFields: false
        },
        OrderLineModificationDeleteData: {
          keyFields: false
        },
        OrderLineModification: {
          keyFields: false
        },
        OrderShippingAvardaShippingBroker: {
          merge: true
        },
        ShopperInsightsSearchHitCheckoutItem: {
          keyFields: false,
        },
        Order: {
          fields: {
            shippingAddress: {
              merge: true
            },
            customerAttribute: {
              merge(existing, incoming) {
                return merge({}, parseCustomerAttribute(existing), parseCustomerAttribute(incoming))
              }
            },
          }
        }
      }
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network"
      }
    }
  })

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}

export { ApolloClientProvider }
